import {useStore} from '../context/StoreContext';
import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, Paper, TextField, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {request, API_URL} from '../services/buyApi';

const fields = {
  name: 'Store name', tagline: 'Tagline', description: 'About the store',
  logo_url: 'Logo image URL (HTTPS)', primary_color: 'Brand color (#RRGGBB)',
  support_email: 'Public support email', shipping_policy: 'Shipping policy',
  returns_policy: 'Returns policy', brand_brief: 'Private brand positioning notes',
};
const longFields = ['description', 'shipping_policy', 'returns_policy', 'brand_brief'];

export default function StoreSetup() {
  const {store}=useStore();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = saved && JSON.stringify(saved.draft) !== JSON.stringify(draft);
  const accept = value => {setSaved(value); setDraft(value.draft); setPreview(null);};
  useEffect(() => {request('/api/staff/profile').then(accept).catch(e => setError(e.message));}, []);

  const act = async fn => {
    setBusy(true); setError(''); setMessage('');
    try {await fn();} catch (e) {setError(e.message);} finally {setBusy(false);}
  };
  const importFile = async file => {
    if (!file) return;
    await act(async () => {
      if (file.size > 100000) throw new Error('Choose a Catalyst export smaller than 100 KB.');
      const data = JSON.parse(await file.text());
      setPreview(await request('/api/staff/profile/catalyst-preview', {
        method:'POST', body:JSON.stringify({package:data}),
      }));
    });
  };

  return <Layout>
    <Typography variant="h4" component="h1" sx={{my:3}}>Store setup</Typography>
    <Button component={Link} to="/admin">Back to orders and events</Button>
    {error && <Alert severity="error" sx={{my:2}}>{error}</Alert>}
    {message && <Alert severity="success" sx={{my:2}}>{message}</Alert>}
    {!draft ? <Button href={API_URL+'/api/auth/login'}>Sign in with VentES</Button> : <>
      <Paper sx={{p:3, my:3}}>
        <Typography variant="h6">Bring your Catalyst work into BUY</Typography>
        <Typography paragraph>In Catalyst, approve your business foundation, preview BUY setup, then approve and download it. Select that file here to review suggested store details. Your brand-positioning notes stay private.</Typography>
        <Button component="label" disabled={busy || dirty}>
          Choose Catalyst setup file
          <input hidden type="file" accept="application/json,.json" disabled={busy || dirty} onChange={e => {importFile(e.target.files?.[0]); e.target.value='';}}/>
        </Button>
        {dirty && <Typography>Save your current draft before importing.</Typography>}
        {preview && <Box sx={{my:2}}>
          <Alert severity="info">{preview.message}</Alert>
          {preview.changes.map(change => <Box key={change.field} sx={{my:2}}>
            <Typography fontWeight="bold">{fields[change.field]}</Typography>
            <Typography sx={{whiteSpace:'pre-wrap'}}>Current: {change.before || '(empty)'}</Typography>
            <Typography sx={{whiteSpace:'pre-wrap'}}>Suggested: {change.after}</Typography>
          </Box>)}
          <Button disabled={busy} onClick={() => {setDraft(preview.suggested); setPreview(null); setMessage('Suggestions added to the editor. Review and save the draft.');}}>Use these suggestions</Button>
          <Button onClick={() => setPreview(null)}>Cancel import</Button>
        </Box>}
      </Paper>
      <Paper component="form" sx={{p:3}} onSubmit={e => {e.preventDefault(); act(async () => {
        accept(await request('/api/staff/profile', {method:'PUT', body:JSON.stringify({version:saved.version, profile:draft})}));
        setMessage('Draft saved. Review the preview before publishing.');
      });}}>
        <Typography variant="h6">Branding and store information</Typography>
        {Object.entries(fields).map(([key,label]) => <TextField key={key} label={label} value={draft[key] || ''}
          required={key==='name'} fullWidth margin="normal" disabled={busy}
          multiline={longFields.includes(key)} minRows={longFields.includes(key)?3:undefined}
          onChange={e => {setDraft({...draft,[key]:e.target.value}); setPreview(null);}}/>)}
        <Button type="submit" variant="contained" disabled={busy}>Save draft</Button>
      </Paper>
      <Paper sx={{p:3,my:3}}>
        <Typography variant="h6">Public content preview</Typography>
        {draft.logo_url?.startsWith('https://') && <Box component="img" src={draft.logo_url} alt={draft.name+' logo'} referrerPolicy="no-referrer" sx={{maxWidth:180,maxHeight:100}}/>}
        <Typography variant="h4">{draft.name}</Typography>
        <Typography variant="h6">{draft.tagline}</Typography>
        {['description','support_email','shipping_policy','returns_policy'].map(key => <Typography key={key} sx={{my:2,whiteSpace:'pre-wrap'}}>{draft[key]}</Typography>)}
        <Typography paragraph>Publishing updates public branding and policy text. Checkout availability, shipping charges, pickup arrangements and product stock are configured separately.</Typography>
        <Button variant="contained" disabled={busy || dirty || !saved.version} onClick={() => act(async () => {
          accept(await request('/api/staff/profile/publish', {method:'POST',body:JSON.stringify({version:saved.version})}));
          setMessage('Store details published. Open the shop to see them.');
        })}>Publish reviewed details</Button>
        <Button href={store?.store_url||"/"} target="_blank" rel="noopener">Open shop</Button>
      </Paper>
    </>}
  </Layout>;
}
